import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import FeedbackButton from "components/FeedbackButton";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { Gear, Plus } from "components/icons/generated";
import Box, { BoxProps } from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import Tab from "ds/components/Tab";
import TextEllipsis from "ds/components/TextEllipsis";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";

import { showDeleteConfirmation } from "./DeleteConfirmation";
import {
  DASHBOARD_HEADER_PENDO_FEEDBACK_ID,
  DEFAULT_TAB_IDS,
  NEW_TAB_IN_LIST_ID,
} from "./constants";
import { DashboardContext } from "./context";
import styles from "./styles.module.css";
import useCurrentTab from "./useCurrentTab";

type DashboardViewHeaderProps = Omit<BoxProps, "children"> & { children?: ReactNode };

function DashboardViewHeader({ children, ...rest }: DashboardViewHeaderProps) {
  const navigate = useNavigate();
  const { canAddMoreViews, openManageDrawer, addNewTab, removeTab, configLoading, tabs } =
    useTypedContext(DashboardContext);

  const currentTab = useCurrentTab();

  const handleClick = (path: string) => {
    navigate(path);
  };

  const handleTabDelete = (id: string, title: string) => {
    showDeleteConfirmation({ removeTabCallback: () => removeTab(id), name: title });
  };

  const addNewTabHandler = () => {
    if (canAddMoreViews) {
      addNewTab();
    }
  };

  return (
    <ViewHeader firstLevel>
      <Box align="start" justify="between" fullWidth>
        <Box gap="medium" direction="column" grow="1" fullWidth>
          <ViewHeaderWrapper justify="between" align="center">
            <ViewHeaderTitle>Dashboard</ViewHeaderTitle>
            <Box gap="medium">
              <FeedbackButton id={DASHBOARD_HEADER_PENDO_FEEDBACK_ID} />
              <ButtonIcon
                variant="secondary"
                icon={Gear}
                disabled={configLoading}
                size="medium"
                onPress={() => openManageDrawer(undefined, { method: "Header button click" })}
              >
                Manage view
              </ButtonIcon>
            </Box>
          </ViewHeaderWrapper>
          <Box className={styles.tabsContainerScollable}>
            <Box gap="medium" className={styles.tabsContainer}>
              {tabs.map(({ id, title }) => (
                <div key={id} className={styles.tabWithActionWrapper}>
                  <Tab
                    isActive={currentTab === id}
                    onClick={() => handleClick(`/dashboard?tab=${id}`)}
                    id={id}
                    aria-label={title}
                    className={styles.tabWithAction}
                    label={
                      <TextEllipsis tooltip={title} tooltipWidthMode="maxWidthSm">
                        {(props) => (
                          <Typography {...props} tag="span" variant="p-t7">
                            {title}
                          </Typography>
                        )}
                      </TextEllipsis>
                    }
                  />
                  <DropdownMenuEllipsis
                    className={styles.tabWithActionDropdownTrigger}
                    tooltip="Settings"
                    buttonVariant="ghost"
                  >
                    <DropdownMenuItem onAction={openManageDrawer}>Manage view</DropdownMenuItem>
                    {!DEFAULT_TAB_IDS.includes(id) && (
                      <DropdownMenuItem danger onAction={() => handleTabDelete(id, title)}>
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuEllipsis>
                </div>
              ))}
              <Tab
                id={NEW_TAB_IN_LIST_ID}
                onClick={addNewTabHandler}
                aria-label={canAddMoreViews ? "Add new tab" : "Views limit exceeded"}
                isActive={false}
                label={
                  <Tooltip on={(props) => <Icon {...props} src={Plus} />}>
                    {canAddMoreViews ? "Add new tab" : "Views limit exceeded"}
                  </Tooltip>
                }
                disabled={!canAddMoreViews}
              />
            </Box>
          </Box>
        </Box>
        <Box {...rest}>{children}</Box>
      </Box>
    </ViewHeader>
  );
}

export default DashboardViewHeader;
